import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '@/hooks/use-auth';
import { trackProductEvent } from '@/lib/analytics/product-events';
import { campaignService } from '@/services/data/CampaignService';
import type { ProjectKind } from '@/services/data/CampaignService';
import { logger } from '@/utils/logger';

export type CreatePlanSeedKind = 'week' | 'launch' | 'gaps' | 'other';
export type CreatePlanEntry =
  'list_primary_action' | 'starter_card' | 'manual_dialog' | 'manual_link';

export interface CreatePlanArgs {
  /** Optional seed prompt — passed via `?seed=` to PlanStrategyComposer. */
  seedPrompt?: string;
  /** Optional name override (defaults to 'Untitled project'). Used by manual dialog. */
  name?: string;
  /** Optional goal text. Persists to `campaigns.goal`. Used by manual dialog. */
  goal?: string;
  /** Optional custom instructions. Persists to `campaigns.notes`. */
  notes?: string;
  /** Optional Phosphor icon id from FOLDER_ICONS. */
  icon?: string | null;
  /** Optional FOLDER_COLORS id. */
  color?: string | null;
  /** Telemetry: which entry point fired this mutation. */
  entry?: CreatePlanEntry;
  /** Project kind discriminator (PJ-1). Defaults to 'campaign' server-side. */
  kind?: ProjectKind;
  /** BV-1 W6: optional pinned brand voice id (project-level). */
  brandVoiceId?: string | null;
  /** BK-1: optional pinned brand kit id (project-level). */
  brandKitId?: string | null;
}

function deriveSeedKind(seed: string): CreatePlanSeedKind {
  const lower = seed.toLowerCase();
  if (lower.includes('week')) return 'week';
  if (lower.includes('launch')) return 'launch';
  if (lower.includes('fill') || lower.includes('gap')) return 'gaps';
  return 'other';
}

export function useCreatePlan() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async ({
      seedPrompt,
      name,
      goal,
      notes,
      icon,
      color,
      entry,
      kind,
      brandVoiceId,
      brandKitId,
    }: CreatePlanArgs = {}) => {
      if (!session?.user?.id) throw new Error('Not authenticated');
      const result = await campaignService.createCampaign({
        userId: session.user.id,
        name: name?.trim() || 'Untitled project',
        goal: goal?.trim() || undefined,
        notes: notes?.trim() || undefined,
        icon: icon ?? undefined,
        color: color ?? undefined,
        kind,
        brandVoiceId: brandVoiceId ?? null,
        // NOT `?? null`. `createCampaign` distinguishes the two: `undefined` means "stamp
        // the active workspace" and resolves it, while an explicit `null` means "deliberately
        // unscoped" and is written through unchanged. Coercing here collapsed that
        // distinction and defeated the default for every caller that omits the field —
        // `CreatePlanCard` (the starter-card quick create) does exactly that, so it wrote an
        // unstamped project on every use.
        brandKitId,
      });
      if (result.error) throw result.error;
      if (!result.data) throw new Error('No campaign returned');
      return { campaign: result.data, seedPrompt, name, goal, entry };
    },
    onSuccess: ({ campaign, seedPrompt, name, goal, entry }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', session?.user?.id] });
      const collectionId = campaign.collectionId || campaign.id;
      const search = new URLSearchParams();
      search.set('planId', campaign.id);
      search.set('tab', 'calendar');
      if (seedPrompt) search.set('seed', seedPrompt);
      navigate(`/projects/${collectionId}?${search.toString()}`);
      const resolvedEntry: CreatePlanEntry =
        entry ?? (seedPrompt ? 'starter_card' : 'list_primary_action');
      const eventPayload = {
        plan_id: campaign.id,
        project_id: campaign.id,
        entry: resolvedEntry,
        seeded: !!seedPrompt,
        seed_kind: seedPrompt ? deriveSeedKind(seedPrompt) : null,
        named: !!name?.trim(),
        has_goal: !!goal?.trim(),
      };
      void trackProductEvent('plan_created', eventPayload);
      void trackProductEvent('project_created', eventPayload);
    },
    onError: (err) => {
      logger.error('Plan create failed', err);
      toast.error("Couldn't create project. Try again?");
    },
  });
}
